import authorizationUtil from '@/utils/authorization-util';
import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';

let userManager: UserManager | null = null;
let configPromise: Promise<UserManager> | null = null;
let ssoConfig: SSOConfig | null = null;

export interface IdentityProvider {
  alias: string;
  display_name: string;
  provider_id: string;
}

interface SSOConfig {
  authority: string;
  client_id: string;
  redirect_uri: string;
  post_logout_redirect_uri: string;
  response_type: string;
  scope: string;
  identity_providers?: IdentityProvider[];
}

async function fetchSSOConfig(): Promise<SSOConfig> {
  if (ssoConfig) return ssoConfig;
  const response = await fetch('/v1/user/sso/config');
  const result = await response.json();
  if (result.code !== 0) {
    throw new Error(result.message || 'Failed to fetch SSO config');
  }
  ssoConfig = result.data;
  return result.data;
}

/** Identity providers (Google, etc.) configured in the Keycloak realm. */
export async function getIdentityProviders(): Promise<IdentityProvider[]> {
  try {
    const config = await fetchSSOConfig();
    return config.identity_providers ?? [];
  } catch {
    return [];
  }
}

/** Decode the payload of a JWT without verifying its signature. */
function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const payload = token.split('.')[1];
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(normalized)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(json);
  } catch {
    return {};
  }
}

export async function getOidcManager(): Promise<UserManager> {
  if (userManager) return userManager;

  // Prevent multiple concurrent config fetches
  if (configPromise) return configPromise;

  configPromise = (async () => {
    const config = await fetchSSOConfig();

    // Use browser origin for redirect URIs since the backend may report
    // its own host (e.g. port 9380) instead of the frontend's (e.g. port 8000)
    const origin = window.location.origin;

    userManager = new UserManager({
      authority: config.authority,
      client_id: config.client_id,
      redirect_uri: `${origin}/login-next/callback`,
      post_logout_redirect_uri: `${origin}/login-next`,
      response_type: config.response_type || 'code',
      scope: config.scope || 'openid email profile',
      automaticSilentRenew: true,
      userStore: new WebStorageStateStore({ store: localStorage }),
    });

    return userManager;
  })();

  return configPromise;
}

/**
 * Start the OIDC authorization-code redirect.
 *
 * Pass an `idpHint` (Keycloak identity-provider alias, e.g. "google") to make
 * Keycloak skip its own login page and bounce straight to that provider —
 * turning social login into a single click.
 */
export async function signinRedirect(idpHint?: string): Promise<void> {
  const mgr = await getOidcManager();
  await mgr.signinRedirect(
    idpHint ? { extraQueryParams: { kc_idp_hint: idpHint } } : undefined,
  );
}

export async function signinCallback(): Promise<User> {
  const mgr = await getOidcManager();
  let user: User;
  try {
    user = await mgr.signinRedirectCallback();
  } finally {
    // Security: Clear URL parameters after processing to prevent exposure
    // This removes session_state, code, and other sensitive OAuth parameters
    // from browser history, preventing them from being logged or leaked
    // Using finally ensures cleanup happens even if signinRedirectCallback fails
    if (window.history.replaceState) {
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }

  return user;
}

/**
 * Sign in directly with username + password via the Keycloak token endpoint
 * (OAuth2 Resource Owner Password Credentials / Direct Access Grant).
 *
 * Requires "Direct Access Grants Enabled" on the Keycloak client. The resulting
 * tokens are stored in the same shape the redirect flow uses, so the rest of the
 * app (token interceptor, silent renew) keeps working unchanged.
 *
 * Note: this flow cannot perform social login (e.g. Google) or MFA — use
 * signinRedirect() for those.
 */
export async function passwordSignin(
  username: string,
  password: string,
): Promise<User> {
  const config = await fetchSSOConfig();
  const mgr = await getOidcManager();

  const tokenEndpoint = `${config.authority.replace(/\/$/, '')}/protocol/openid-connect/token`;
  const body = new URLSearchParams({
    grant_type: 'password',
    client_id: config.client_id,
    username,
    password,
    scope: config.scope || 'openid email profile',
  });

  const res = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.access_token) {
    const description =
      data.error_description || data.error || 'Invalid username or password';
    throw new Error(description);
  }

  const profile = data.id_token
    ? decodeJwtPayload(data.id_token)
    : decodeJwtPayload(data.access_token);

  const user = new User({
    id_token: data.id_token,
    session_state: data.session_state ?? null,
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    token_type: data.token_type || 'Bearer',
    scope: data.scope,
    profile: profile as User['profile'],
    expires_at: Math.floor(Date.now() / 1000) + (data.expires_in || 0),
  });

  await mgr.storeUser(user);
  return user;
}

/**
 * Exchange an authenticated OIDC user for a backend session and persist the
 * resulting user info. Shared by the SSO redirect callback and the embedded
 * password login.
 */
export async function completeSsoSession(user: User): Promise<void> {
  try {
    const response = await fetch('/v1/user/sso/callback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ access_token: user.access_token }),
    });
    const result = await response.json();
    if (result.code === 0 && result.data?.user) {
      authorizationUtil.setUserInfo({
        name:
          result.data.user.nickname || user.profile?.preferred_username || '',
        email: result.data.user.email || user.profile?.email || '',
        avatar: result.data.user.avatar || '',
      });

      if (result.data.pending_deletion_count > 0) {
        localStorage.setItem(
          'pending_deletion_count',
          String(result.data.pending_deletion_count),
        );
      } else {
        localStorage.removeItem('pending_deletion_count');
      }
    }
  } catch {
    // Best-effort; load_user handles provisioning on next API call
  }
}

/**
 * RP-initiated logout at Keycloak. Pass the user's `id_token` as `idTokenHint`
 * so Keycloak terminates the session and redirects back immediately, skipping
 * its "Do you want to log out?" confirmation page. Capture the hint *before*
 * clearing local OIDC data, since the token is needed to build the URL.
 */
export async function signoutRedirect(idTokenHint?: string): Promise<void> {
  const mgr = await getOidcManager();
  await mgr.signoutRedirect(
    idTokenHint ? { id_token_hint: idTokenHint } : undefined,
  );
}

export async function getUser(): Promise<User | null> {
  const mgr = await getOidcManager();
  return mgr.getUser();
}

/**
 * True when this browser holds a signed-in OIDC user, without constructing a
 * UserManager (which would fetch the SSO config) to find out.
 */
function hasStoredOidcUser(): boolean {
  return Object.keys(localStorage).some((k) => k.startsWith('oidc.user:'));
}

/**
 * Re-arm automatic token renewal for a session that already exists.
 *
 * `automaticSilentRenew` only starts its timer when a UserManager raises the
 * "user loaded" event, and the only calls that do that are the sign-in paths.
 * Nothing else in the app constructed a UserManager at all — so after any full
 * page load (a reload, a bookmark, a new tab) there was no manager, no timer,
 * and no renewal. The access token then expired on the realm's clock,
 * getOidcAccessTokenSync() started returning null, the next request went out
 * with no Authorization header, and the 401 handler in utils/request.ts wiped
 * storage and bounced the user to the login page. That is the "logged out
 * again after a few minutes" report: not a session policy, a missing timer.
 *
 * signinSilent() rather than getUser(): it refreshes through the stored refresh
 * token immediately (so a token that expired while the tab was closed is
 * replaced instead of merely observed), and storing the result raises the load
 * event that arms the renewal timer for the rest of the session.
 *
 * Resolves either way. A failure here means the refresh token is gone or the
 * session ended server-side, and the right answer to that is the ordinary 401
 * path on the next request, not an error on a page that may not even need auth.
 */
export async function resumeSilentRenew(): Promise<void> {
  if (!hasStoredOidcUser()) return;
  try {
    const mgr = await getOidcManager();
    await mgr.signinSilent();
  } catch {
    // Deliberately quiet — see above.
  }
}

export function getOidcIdTokenSync(): string | null {
  // Read the OIDC id_token from localStorage synchronously, used as the
  // id_token_hint for RP-initiated logout.
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith('oidc.user:')) {
      try {
        const userData = JSON.parse(localStorage.getItem(key) || '');
        if (userData?.id_token) {
          return userData.id_token;
        }
      } catch {
        // ignore parse errors
      }
    }
  }
  return null;
}

export function getOidcAccessTokenSync(): string | null {
  // Read OIDC token from localStorage synchronously
  // oidc-client-ts stores user data with keys like "oidc.user:<authority>:<client_id>"
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith('oidc.user:')) {
      try {
        const userData = JSON.parse(localStorage.getItem(key) || '');
        if (userData?.access_token && !isTokenExpired(userData)) {
          return userData.access_token;
        }
      } catch {
        // ignore parse errors
      }
    }
  }
  return null;
}

function isTokenExpired(userData: { expires_at?: number }): boolean {
  if (!userData.expires_at) return false;
  return userData.expires_at < Math.floor(Date.now() / 1000);
}

export function removeOidcData(): void {
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith('oidc.user:') || key.startsWith('oidc.')) {
      localStorage.removeItem(key);
    }
  }
}
